import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
} from "@mui/material";
import Sidebar from "./Sidebar";
import Navbar from "./Navbar";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import LaptopMacIcon from "@mui/icons-material/LaptopMac";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import { NAVY, SLATE, GOLD, PANEL_BLUE_END, BG } from "../theme";

const Dashboard = () => {
  const navigate = useNavigate();
  const [totalAssets, setTotalAssets] = useState(0);
  const [totalDebitcards, setTotalDebitcards] = useState(0);
  const userData = JSON.parse(localStorage.getItem("user"));

  const getDebitCardRoute = () => {
    if (
      allowedModules.some((m) =>
        ["HO_Report", "HO_Assign", "HO_Recorn"].includes(m),
      )
    ) {
      return "/ho-user/horeport";
    }

    if (
      allowedModules.some((m) =>
        ["RO_Report", "RO_Accept", "RO_Assign"].includes(m),
      )
    ) {
      return "/ro-user/roreport";
    }

    if (
      allowedModules.some((m) =>
        ["BO_Report", "BO_Accept", "BO_Assign"].includes(m),
      )
    ) {
      return "/bo-user/boreport";
    }

    return "/debit-card";
  };

  const allowedModules = JSON.parse(
    localStorage.getItem("allowedModules") || "[]",
  );

  const hasAMSAccess = allowedModules.some((m) =>
    [
      "IT-HOME",
      "IT-APPROVAL",
      "IT-NEW ASSETS",
      "IT-Bulk Upload",
      "IT-Asset Depreciation",
      "IT-User Roles",
    ].includes(m),
  );

  const hasDebitCardAccess = allowedModules.some((m) =>
    [
      "HO_Report",
      "HO_Assign",
      "HO_Recorn",
      "RO_Report",
      "RO_Accept",
      "RO_Assign",
      "BO_Report",
      "BO_Accept",
      "BO_Assign",
    ].includes(m),
  );

  const hasTrackAccess = allowedModules.some((m) =>
    ["CALL_ADMIN", "CALL_Telecaller", "CALL_Branch"].includes(m),
  );

  // Build modules dynamically
  // Colors now pull from the shared theme triad (SLATE / GOLD / PANEL_BLUE_END)
  // instead of the old green/purple/orange, so this matches Login.js
  const modules = [];

  if (hasAMSAccess) {
    modules.push({
      title: "Asset Management System",
      description:
        "Manage all company assets, allocation, returns, maintenance and more.",
      icon: <LaptopMacIcon sx={{ fontSize: 45 }} />,
      color: SLATE,
      route: "/AMSDashBoard",
      buttonText: "Go to AMS",
      features: ["Asset Allocation", "Asset Returns", "Maintenance", "Reports"],
    });
  }

  if (hasDebitCardAccess) {
    modules.push({
      title: "Debit Card Distribution",
      description:
        "Manage debit card inventory, distribution, activation and tracking.",
      icon: <CreditCardIcon sx={{ fontSize: 45 }} />,
      color: GOLD,
      route: getDebitCardRoute(),
      buttonText: "Go to Debit Card Module",
      features: [
        "Card Inventory",
        "Card Distribution",
        "Activation Status",
        "Reports",
      ],
    });
  }

  if (hasTrackAccess) {
    modules.push({
      title: "Call Track Module",
      description:
        "Monitor call activities, field user status, live locations, routes, and visit tracking.",
      icon: <LocationOnIcon sx={{ fontSize: 45 }} />,
      color: PANEL_BLUE_END,
      route: "/track/Dashboard",
      buttonText: "Go to Call Track Module",
      features: [
        "Call Tracking",
        "Live Tracking",
        "Route History",
        "Visit Logs",
        "Reports",
      ],
    });
  }

  const location = useLocation();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isTotalAssetsPage = location.pathname.includes("/total-assets");

  const { API_CONFIG, REFRESH_CONFIG } = require("../configuration");

  useEffect(() => {
    const rawbranches = localStorage.getItem("branchesAssigned");
    let branchesAssigned = [];

    try {
      const parsed = JSON.parse(rawbranches);
      if (Array.isArray(parsed)) {
        branchesAssigned = parsed;
      } else if (typeof parsed === "string") {
        branchesAssigned = [parsed];
      }
    } catch (err) {
      console.error("Invalid branchesAssigned in localStorage:", rawbranches);
    }

    axios
      .get(`${API_CONFIG.APIURL}/dashboard/summary`, {
        headers: {
          branchesAssigned: branchesAssigned.join(","),
          empid: userData?.emp_id,
          designation: userData?.designation,
        },
      })
      .then((response) => {
        setTotalAssets(response.data.totalAssets);
        setTotalDebitcards(response.data.totalDebitcards);
        setAssets(response.data.summary);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching asset data:", error);
        setError("Welcome To Module Management");
        setLoading(false);
      });
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: BG,
      }}
    >
      <Navbar />
      <Sidebar />

      <Box
        sx={{
          p: { xs: 2, md: 4 },
          ml: { xs: 0, md: "250px" },
          width: {
            xs: "100%",
            md: "calc(100% - 250px)",
          },
          boxSizing: "border-box",
          overflowX: "hidden",
        }}
      >
        {/* Welcome strip — echoes the "WELCOME BACK" eyebrow + gold rule from Login.js */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              color: GOLD,
              letterSpacing: "1px",
              mb: 0.5,
            }}
          >
            OVERVIEW
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color: NAVY }}>
            {userData?.name ? `Welcome, ${userData.name}` : "Welcome"}
          </Typography>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3} mb={4}>
          {hasAMSAccess && (
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  borderRadius: 4,
                  boxShadow: "0 4px 15px rgba(4,16,33,0.08)",
                  border: "1px solid #e5e7eb",
                }}
              >
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Total Assets
                      </Typography>

                      <Typography variant="h4" fontWeight={700} mt={1} sx={{ color: NAVY }}>
                        {totalAssets}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 3,
                        bgcolor: `${SLATE}20`,
                        color: SLATE,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Inventory2Icon fontSize="large" />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {hasDebitCardAccess && (
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  borderRadius: 4,
                  boxShadow: "0 4px 15px rgba(4,16,33,0.08)",
                  border: "1px solid #e5e7eb",
                }}
              >
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Total Debit Cards
                      </Typography>

                      <Typography variant="h4" fontWeight={700} mt={1} sx={{ color: NAVY }}>
                        {totalDebitcards}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 3,
                        bgcolor: `${GOLD}20`,
                        color: GOLD,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <CreditCardIcon fontSize="large" />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {hasTrackAccess && (
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  borderRadius: 4,
                  boxShadow: "0 4px 15px rgba(4,16,33,0.08)",
                  border: "1px solid #e5e7eb",
                }}
              >
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Active Field Users
                      </Typography>

                      <Typography variant="h4" fontWeight={700} mt={1} sx={{ color: NAVY }}>
                        128
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 3,
                        bgcolor: `${PANEL_BLUE_END}20`,
                        color: PANEL_BLUE_END,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <LocationOnIcon fontSize="large" />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Module Overview */}
        {modules.length > 0 && (
          <Typography variant="h5" fontWeight={700} mb={3} sx={{ color: NAVY }}>
            Module Overview
          </Typography>
        )}

        <Grid container spacing={3}>
          {modules.length > 0 ? (
            modules.map((module, index) => (
              <Grid item xs={12} sm={6} lg={4} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: 4,
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 4px 15px rgba(4,16,33,0.08)",
                    transition: "0.3s",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: "0 12px 25px rgba(4,16,33,0.15)",
                    },
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: 3,
                        bgcolor: `${module.color}15`,
                        color: module.color,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      {module.icon}
                    </Box>

                    <Typography variant="h6" fontWeight={700} mb={1} sx={{ color: NAVY }}>
                      {module.title}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" mb={2}>
                      {module.description}
                    </Typography>

                    <Box mb={3}>
                      {module.features.map((feature, idx) => (
                        <Typography
                          key={idx}
                          sx={{
                            mb: 1,
                            color: "#475569",
                            fontSize: "14px",
                          }}
                        >
                          ✓ {feature}
                        </Typography>
                      ))}
                    </Box>

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => navigate(module.route)}
                      sx={{
                        bgcolor: module.color,
                        borderRadius: 2,
                        py: 1.2,
                        textTransform: "none",
                        fontWeight: 600,
                        "&:hover": {
                          bgcolor: module.color,
                          opacity: 0.9,
                        },
                      }}
                    >
                      {module.buttonText}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Card
                sx={{
                  p: 4,
                  textAlign: "center",
                  borderRadius: 4,
                  border: "1px solid #e5e7eb",
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  No modules assigned to your account
                </Typography>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default Dashboard;